/**
 * THE REQUIREMENTS DOCUMENT, ON THE SCREEN — the documents programme, increments
 * 2a (reader) and 2b (produce, snapshot, download).
 *
 * A REVIEWER'S DOCUMENT, GENERATED WHOLE. The list of requirements is worked out
 * from the backlog on every render (requirements.ts), grouped by area and marked
 * with how essential each is for the release (MoSCoW). The narrative around it —
 * purpose, scope, stakeholders, assumptions — is the project's own charter fields,
 * read live from the project. So there is nothing to fill in twice: the document IS
 * the board and the charter, set out the way a reviewer reads them.
 *
 * IT BECOMES A FILE, THE SAME WAY THE CHANGE REQUEST DOES — increment 2b. While it
 * is a draft it reads the board and About live, which is what lets a wrong line be
 * fixed by correcting the card. Producing the PDF snapshots the whole composed
 * document into Doc.requirements (migration 0114) and uploads the file, so from
 * that point on the on-screen document is the one in the file and the one that gets
 * signed — 0114 freezes the snapshot the moment it leaves draft, the same as a
 * change request's scope. Once signed, the approved copy — the signed file with a
 * Record of Approval page — can be downloaded, exactly as the change request's can.
 *
 * ONE VOCABULARY WITH THE CARD. The release chips are the same `.moscow-chip` the
 * card wears, so "Must have" means the same thing on the board and in the document.
 */
import { useState } from 'react';
import { useApp } from '../store';
import { requirementsFrom, composeRequirements, type RequirementsDoc } from '../requirements';
import { displayName } from '../people';
import { requirementsPdf } from '../requirements-pdf';
import { approvedCopy, type SignatureRow } from '../change-request-pdf';
import { asDate, asTime } from '../change-request';
import { isEditable, isRejected, DOCUMENT_STATE } from '../documents';
import { MOSCOW_ORDER, MOSCOW_SHORT } from '../types';
import type { Doc } from '../types';
import RichTextEditor from './RichTextEditor';
import { richHasText, type RichDoc } from '../rich-text';

/** A requirements document is generated from the backlog, like the change
 *  request — this is the predicate the Documents row dispatches on. */
export const isRequirements = (doc: Doc): boolean => doc.kind === 'requirements';

/** A rich-text charter field — scope, assumptions, constraints (migration 0113) —
 *  rendered read-only. An unset field says so; while the document is still a draft
 *  it offers the ONE link that fills it, so a reviewer who spots a gap is never left
 *  hunting for where it is set. Once the document is produced the snapshot is fixed,
 *  so there is nothing to jump to and no link is shown. */
function CharterField({ value, onEdit }: { value?: RichDoc | null; onEdit?: () => void }) {
  if (!richHasText(value))
    return (
      <p className="reqdoc-p is-empty">
        Not set in the charter yet.{' '}
        {onEdit && (
          <button type="button" className="reqdoc-setlink" onClick={onEdit}>
            Set it in Project details &rarr;
          </button>
        )}
      </p>
    );
  return (
    <div className="reqdoc-rich">
      <RichTextEditor value={value} editable={false} />
    </div>
  );
}

export function RequirementsDocument({
  doc,
  mayEdit,
  onProblem,
}: {
  doc: Doc;
  mayEdit: boolean;
  onProblem: (why: string) => void;
}) {
  const issues = useApp((s) => s.issues);
  const projectName = useApp((s) => s.meta.name);
  const projectId = useApp((s) => s.projectId);
  const project = useApp((s) => s.projects.find((p) => p.id === projectId));
  const meta = useApp((s) => s.meta);
  const signoffs = useApp((s) => s.signoffs);
  const user = useApp((s) => s.user);
  const profiles = useApp((s) => s.profiles);
  const drillTo = useApp((s) => s.drillTo);
  const saveDocument = useApp((s) => s.saveDocument);
  const uploadDocumentFile = useApp((s) => s.uploadDocumentFile);
  const documentFileUrl = useApp((s) => s.documentFileUrl);
  const projectLogoUrl = useApp((s) => s.projectLogoUrl);
  const recordAudit = useApp((s) => s.recordAudit);
  const [making, setMaking] = useState(false);

  /* The one link a blank scope/assumptions/constraints section offers: straight to
     the Plan's About editor, opened on arrival. Only while it is still a draft. */
  const editAbout = () => drillTo('plan', 'about');

  /* Only the cards it covers — a document with no sprint is the whole project,
     the same choice the change request and the document list already make. */
  const covered = doc.sprint ? issues.filter((i) => i.sprint === doc.sprint) : issues;
  const live = composeRequirements(project, requirementsFrom(covered));

  /*
   * LIVE WHILE IT IS A DRAFT, FROZEN ONCE IT IS NOT — the change request's rule.
   *
   * A draft composes the board and About on every render, which is what lets a
   * wrong line be fixed by correcting the card. The moment it is produced (and
   * sent) it shows the snapshot taken when the PDF was made instead — the document
   * as it was signed, not as it stands now. A document produced before the snapshot
   * existed has none and falls back to live, which is the old behaviour.
   */
  const model: RequirementsDoc = isEditable(doc) ? live : (doc.requirements ?? live);
  const req = model.requirements;
  const showSetLinks = mayEdit; // only offer "set it in About" while it can still change

  const areaCount = req.areas.length;
  const summaryLine =
    `${req.total} requirement${req.total === 1 ? '' : 's'}` +
    ` across ${areaCount} area${areaCount === 1 ? '' : 's'}` +
    (req.deferred.length ? `, ${req.deferred.length} held back` : '') +
    (req.counts.unset ? ` · ${req.counts.unset} not yet weighed for the release` : '');

  /* WHO SIGNED, frozen onto the document once it was sent — the same rows the
     Sign-offs page reads. The Record of Approval page is drawn from these. */
  const rows = signoffs.filter((r) => r.documentId === doc.id).sort((a, b) => a.seq - b.seq);
  const signatures: SignatureRow[] = rows.map((r) => ({
    title: r.position ?? '',
    name: r.name ?? '',
    how: !r.signedAt
      ? ''
      : r.answer === 'rejected'
        ? 'Rejected'
        : r.onBehalfBy
          ? `Approved on behalf by ${r.onBehalfBy}`
          : r.answeredVia === 'link'
            ? 'Approved by link'
            : 'Approved in FlightDeck',
    signedOn: r.signedAt ? asDate(r.signedAt) : '',
    signedTime: r.signedAt ? asTime(r.signedAt) : '',
  }));
  const anyoneSigned = signatures.some((r) => r.signedOn);

  /* FETCHED HERE AND HANDED TO THE LAYOUT, which reaches for nothing itself. A logo
     that cannot be fetched costs the branding and not the document. */
  const theLogo = async () => {
    if (!meta.logoPath) return undefined;
    try {
      const address = await projectLogoUrl();
      if (!address) return undefined;
      return {
        bytes: await (await fetch(address)).arrayBuffer(),
        type: (meta.logoType === 'jpg' ? 'jpg' : 'png') as 'png' | 'jpg',
      };
    } catch {
      return undefined;
    }
  };

  /*
   * PRODUCE THE PDF — the requirements document becomes the file the sign-off
   * engine signs. Same path the change request takes: render the composed document
   * to bytes, wrap in a File, upload it through the ordinary path (which fingerprints
   * it and refuses a document already sent). The snapshot is taken here, from the
   * same model the PDF was built from, so the on-screen document after signing is the
   * one in the file. Produce only runs on a draft, so both writes are always allowed.
   */
  const produce = async () => {
    setMaking(true);
    try {
      const bytes = await requirementsPdf(
        {
          title: doc.title,
          board: projectName,
          /* Stamped once here rather than read inside the generator, so the same
             document twice is the same file. */
          preparedOn: asDate(doc.createdAt),
          preparedBy: user?.email ? displayName(user.email, profiles) : '',
          version: String(doc.version ?? 1),
          status: DOCUMENT_STATE[doc.state],
          logo: await theLogo(),
        },
        live
      );
      const name = `${
        doc.title.replace(/[^\w\-.]+/g, ' ').replace(/\s+/g, ' ').trim() || 'Requirements'
      }.pdf`;
      const file = new File([bytes as BlobPart], name, { type: 'application/pdf' });
      const up = await uploadDocumentFile(doc.id, file);
      onProblem(up || (await saveDocument(doc.id, { requirements: live })));
    } catch (e) {
      onProblem((e as { message?: string })?.message ?? 'The document could not be produced.');
    }
    setMaking(false);
  };

  /*
   * THE APPROVED COPY, A DOWNLOAD RATHER THAN A REPLACEMENT — the same generic
   * Record of Approval the change request and charter append. The signed file is the
   * one people signed and the rules refuse to change it; the record of approval is
   * that same file with a page added, handed to whoever asks.
   */
  const download = async () => {
    setMaking(true);
    try {
      const address = await documentFileUrl(doc.id);
      if (!address) throw new Error('The signed file could not be found.');
      const bytes = await approvedCopy(
        await (await fetch(address)).arrayBuffer(),
        {
          title: doc.title,
          board: projectName,
          sprint: 'The whole project',
          raisedOn: asDate(Date.now()),
          version: String(doc.version ?? 1),
          fileHash: doc.fileHash,
          logo: await theLogo(),
        },
        signatures
      );
      const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(doc.fileName ?? 'Requirements.pdf').replace(/\.pdf$/i, '')} — approved.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      void recordAudit('project', `downloaded the approved copy of “${doc.title}”`).catch(
        (err: unknown) =>
          onProblem(
            `The copy was made, but History could not be told — ${(err as { message?: string })?.message || String(err)}`
          )
      );
    } catch (e) {
      onProblem((e as { message?: string })?.message ?? 'The approved copy could not be made.');
    }
    setMaking(false);
  };

  return (
    <div className="reqdoc">
      {/* The document's own header — what it is, which reading, and where it stands. */}
      <div className="reqdoc-top">
        <p className="reqdoc-eyebrow">Requirements</p>
        <h3 className="reqdoc-h1">{model.projectName} — Requirements</h3>
        <div className="reqdoc-meta">
          <span>
            <b>Project</b> · {model.projectName}
          </span>
          <span>
            <b>Version</b> · {doc.version}
          </span>
          <span>
            <b>Prepared</b> · {asDate(doc.createdAt)}
          </span>
          <span>
            <b>Status</b> · {DOCUMENT_STATE[doc.state]}
          </span>
        </div>
        <p className="reqdoc-summary">{summaryLine}</p>
      </div>

      <div className="reqdoc-body">
        {/* 1 · Purpose */}
        <section className="reqdoc-sec">
          <div className="reqdoc-s-head">
            <span className="reqdoc-s-num">1</span>
            <h4 className="reqdoc-s-title">Purpose</h4>
          </div>
          <CharterField value={model.purpose} onEdit={showSetLinks ? editAbout : undefined} />
          {richHasText(model.successMeasure) && (
            <>
              <p className="reqdoc-mini-h">What success looks like</p>
              <CharterField value={model.successMeasure} onEdit={showSetLinks ? editAbout : undefined} />
            </>
          )}
        </section>

        {/* 2 · Scope */}
        <section className="reqdoc-sec">
          <div className="reqdoc-s-head">
            <span className="reqdoc-s-num">2</span>
            <h4 className="reqdoc-s-title">Scope</h4>
          </div>
          <div className="reqdoc-cols">
            <div>
              <p className="reqdoc-mini-h">In scope</p>
              <CharterField value={model.scopeIn} onEdit={showSetLinks ? editAbout : undefined} />
            </div>
            <div>
              <p className="reqdoc-mini-h">Out of scope (this release)</p>
              <CharterField value={model.scopeOut} onEdit={showSetLinks ? editAbout : undefined} />
            </div>
          </div>
        </section>

        {/* 3 · Stakeholders */}
        <section className="reqdoc-sec">
          <div className="reqdoc-s-head">
            <span className="reqdoc-s-num">3</span>
            <h4 className="reqdoc-s-title">Stakeholders</h4>
          </div>
          {model.sponsor?.trim() || model.stakeholders.length > 0 ? (
            <ul className="reqdoc-people">
              {model.sponsor?.trim() && (
                <li>
                  <b>{model.sponsor.trim()}</b> — Sponsor
                </li>
              )}
              {model.stakeholders.map((s, i) => (
                <li key={i}>
                  <b>{s.name}</b>
                  {s.role?.trim() ? ` — ${s.role.trim()}` : ''}
                </li>
              ))}
            </ul>
          ) : (
            <p className="reqdoc-p is-empty">No stakeholders named in the charter yet.</p>
          )}
        </section>

        {/* 4 · Requirements */}
        <section className="reqdoc-sec">
          <div className="reqdoc-s-head">
            <span className="reqdoc-s-num">4</span>
            <h4 className="reqdoc-s-title">Requirements</h4>
          </div>
          <p className="reqdoc-lead">
            Grouped by area, with each requirement’s release necessity where it has been set.
          </p>
          {req.areas.length === 0 ? (
            <p className="reqdoc-p is-empty">
              No requirements are in {doc.sprint ? 'this sprint' : 'the project'} yet.
            </p>
          ) : (
            req.areas.map((area, ai) => (
              <div className="reqdoc-area" key={area.name}>
                <div className="reqdoc-area-head">
                  <span className="reqdoc-area-no">4.{ai + 1}</span>
                  <span className="reqdoc-area-name">{area.name}</span>
                </div>
                {area.items.map((line, ii) => (
                  <div className="reqdoc-req" key={line.id}>
                    <div className="reqdoc-req-line">
                      <span className="reqdoc-req-no">
                        4.{ai + 1}.{ii + 1}
                      </span>
                      <span className="reqdoc-req-t">{line.title}</span>
                      {line.moscow && (
                        <span className={`moscow-chip is-${line.moscow}`}>{MOSCOW_SHORT[line.moscow]}</span>
                      )}
                    </div>
                    {line.summary && <p className="reqdoc-req-sum">{line.summary}</p>}
                  </div>
                ))}
              </div>
            ))
          )}
        </section>

        {/* 5 · Release necessity — the MoSCoW roll-up (named for the card field,
            NOT "Priority", which is the separate urgency field). */}
        <section className="reqdoc-sec">
          <div className="reqdoc-s-head">
            <span className="reqdoc-s-num">5</span>
            <h4 className="reqdoc-s-title">Release necessity</h4>
          </div>
          <div className="reqdoc-moscow">
            {MOSCOW_ORDER.map((level) => (
              <div className={`reqdoc-mo is-${level}${req.counts[level] === 0 ? ' is-zero' : ''}`} key={level}>
                <div className="reqdoc-mo-n">{req.counts[level]}</div>
                <div className="reqdoc-mo-l">{MOSCOW_SHORT[level]}</div>
              </div>
            ))}
          </div>
          {req.counts.unset > 0 && (
            <p className="reqdoc-p is-empty">
              {req.counts.unset} requirement{req.counts.unset === 1 ? ' has' : 's have'} not been weighed for
              the release yet — set “Release necessity” on the card to include them here.
            </p>
          )}
        </section>

        {/* 6 · Assumptions & constraints */}
        <section className="reqdoc-sec">
          <div className="reqdoc-s-head">
            <span className="reqdoc-s-num">6</span>
            <h4 className="reqdoc-s-title">Assumptions &amp; Constraints</h4>
          </div>
          <div className="reqdoc-cols">
            <div>
              <p className="reqdoc-mini-h">Assumptions</p>
              <CharterField value={model.assumptions} onEdit={showSetLinks ? editAbout : undefined} />
            </div>
            <div>
              <p className="reqdoc-mini-h">Constraints</p>
              <CharterField value={model.constraints} onEdit={showSetLinks ? editAbout : undefined} />
            </div>
          </div>
        </section>

        {/* 7 · Out of Scope & Deferred */}
        <section className="reqdoc-sec">
          <div className="reqdoc-s-head">
            <span className="reqdoc-s-num">7</span>
            <h4 className="reqdoc-s-title">Out of Scope &amp; Deferred</h4>
          </div>
          {req.deferred.length === 0 ? (
            <p className="reqdoc-p is-empty">Nothing is recorded as held back.</p>
          ) : (
            <>
              <p className="reqdoc-lead">
                Recorded so nothing is set aside silently, with the reason where the board has one.
              </p>
              {req.deferred.map((line) => (
                <div className="reqdoc-req is-deferred" key={line.id}>
                  <div className="reqdoc-req-line">
                    <span className="reqdoc-req-t">{line.title}</span>
                    <span className="moscow-chip is-wont">Deferred</span>
                  </div>
                  {line.why && <p className="reqdoc-req-sum">Reason: {line.why}</p>}
                </div>
              ))}
            </>
          )}
        </section>
      </div>

      {/*
        * PRODUCING THE FILE IS WHAT MAKES IT SENDABLE, and the reason is said in the
        * same place as the button rather than left to the database to refuse — the
        * same rule the release gate keeps. Only offered while it is a draft.
        */}
      {mayEdit && (
        <div className="doc-act cr-produce">
          <button className="btn btn-small" disabled={making} onClick={() => void produce()}>
            {making ? 'Producing…' : doc.filePath ? 'Produce it again' : 'Produce the PDF'}
          </button>
          <span className="doc-hint">
            {doc.filePath
              ? 'Replaces the file attached to this document.'
              : 'Makes the file that gets signed. Nothing can be sent for signature without it.'}
          </span>
        </div>
      )}

      {/* Once signed, the approved copy can be downloaded — the signed file with a
          page recording who approved it and when. NOT when it was rejected: there
          is no approved copy of a refused document, and the way on is to supersede
          it (Vinnie, 30 Sep 2026). */}
      {anyoneSigned && doc.filePath && !isRejected(doc, signoffs) && (
        <div className="doc-act cr-produce">
          <button className="btn btn-small" disabled={making} onClick={() => void download()}>
            {making ? 'Preparing…' : 'Download the approved copy'}
          </button>
          <span className="doc-hint">
            The document exactly as it was signed, with a page recording who approved it and when.
          </span>
        </div>
      )}
    </div>
  );
}
